import React, { useEffect, useMemo, useState } from "react";
import { getFaqs, getFaqCategories, getCategories } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import BrandBand from "@/components/BrandBand";
import Seo, { faqPageLd, breadcrumbLd } from "@/components/Seo";
import { ChevronDown } from "lucide-react";

const CAT_LABELS = {
  "what-is-sibo": "What Is SIBO",
  symptoms: "Symptoms",
  diagnosis: "Diagnosis & Testing",
  treatment: "Treatment",
  diet: "Diet",
  recipes: "Recipes",
  research: "Research",
  general: "General",
};

export default function Faqs() {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState({});
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    getFaqs().then(setAll).catch(() => {});
    getFaqCategories().then(setCategories).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return all.filter((f) => {
      if (cat !== "all" && f.category !== cat) return false;
      if (q) {
        const needle = q.toLowerCase();
        if (!f.question.toLowerCase().includes(needle) && !f.answer.toLowerCase().includes(needle)) return false;
      }
      return true;
    });
  }, [all, q, cat]);

  const grouped = useMemo(() => {
    const map = {};
    for (const f of filtered) {
      const k = f.category || "general";
      if (!map[k]) map[k] = [];
      map[k].push(f);
    }
    return map;
  }, [filtered]);

  const ld = [faqPageLd(all), breadcrumbLd([{ name: "Home", path: "/" }, { name: "FAQs", path: "/faqs" }])];

  return (
    <div data-testid="faqs-page">
      <Seo
        title="Frequently Asked Questions"
        description="Answers to the most common questions about SIBO — symptoms, diagnosis, breath testing, treatment options, diet, and long-term recovery. Clinician-reviewed."
        path="/faqs"
        jsonLd={ld}
        keywords="SIBO FAQ, SIBO symptoms, SIBO test, SIBO treatment, low FODMAP, rifaximin, methane SIBO, IBS vs SIBO"
      />

      <section className="section pb-0">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline">Reference</div>
            <h1 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", lineHeight: 0.95 }} data-testid="faqs-title">
              Frequently asked<br/>questions.
            </h1>
            <p className="mt-10 text-lg" style={{ maxWidth: "38rem" }}>
              Clear, considered answers to the questions people ask most about SIBO — from first symptoms to long-term recovery.
            </p>
          </div>
        </div>
      </section>

      <section className="pt-16 pb-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="rule mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-6">
              <input
                type="text"
                placeholder="Search questions and answers…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="faq-search"
                className="w-full px-4 py-3 border text-sm outline-none"
                style={{ borderColor: "rgba(79,87,70,0.4)", backgroundColor: "transparent" }}
              />
            </div>
            <div className="md:col-span-6 flex flex-wrap gap-x-6 gap-y-3 justify-start md:justify-end">
              <button
                onClick={() => setCat("all")}
                data-testid="faq-cat-all"
                className="overline editorial-link"
                style={{ opacity: cat === "all" ? 1 : 0.55, color: "var(--olive)" }}
              >
                All ({all.length})
              </button>
              {categories.map((c) => (
                <button
                  key={c.category}
                  onClick={() => setCat(c.category)}
                  data-testid={`faq-cat-${c.category}`}
                  className="overline editorial-link"
                  style={{ opacity: cat === c.category ? 1 : 0.55, color: "var(--olive)" }}
                >
                  {CAT_LABELS[c.category] || c.category} ({c.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-9 md:col-start-2">
            {Object.keys(grouped).length === 0 && (
              <div className="font-serif-editorial text-2xl opacity-60 mt-10">No questions match your search.</div>
            )}
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="mb-16" data-testid={`faq-group-${category}`}>
                <div className="overline mb-6">{CAT_LABELS[category] || category}</div>
                <div>
                  {items.map((f) => {
                    const isOpen = !!open[f.id];
                    return (
                      <div key={f.id} className="border-b" style={{ borderColor: "rgba(166,154,139,0.4)" }}>
                        <button
                          onClick={() => {
                            setOpen((o) => {
                              const next = { ...o, [f.id]: !o[f.id] };
                              if (next[f.id]) analytics.faqExpanded(f.question);
                              return next;
                            });
                          }}
                          data-testid={`faq-toggle-${f.id}`}
                          className="w-full py-6 flex items-start justify-between gap-6 text-left"
                          style={{ color: "var(--olive)" }}
                        >
                          <span className="font-serif-editorial" style={{ fontSize: "1.45rem", lineHeight: 1.25, flex: 1 }}>
                            {f.question}
                          </span>
                          <ChevronDown
                            size={20}
                            style={{
                              transition: "transform 250ms ease",
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                              flexShrink: 0,
                              marginTop: "4px",
                            }}
                          />
                        </button>
                        {isOpen && (
                          <div className="pb-8 pr-8 md:pr-16" data-testid={`faq-answer-${f.id}`} style={{ maxWidth: "48rem" }}>
                            <div className="text-base leading-relaxed">{f.answer}</div>
                            {f.references && f.references.length > 0 && (
                              <div className="mt-6 pt-5 border-t" style={{ borderColor: "rgba(166,154,139,0.35)" }} data-testid={`faq-refs-${f.id}`}>
                                <div className="overline mb-3">References</div>
                                <ul className="space-y-1.5">
                                  {f.references.map((r, ri) => (
                                    <li key={ri} className="text-sm opacity-80" style={{ lineHeight: 1.5 }}>— {r}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandBand />
    </div>
  );
}
